import {
  AUTHENTICATOR_ATTR_RE,
  CAPTCHA_ATTR_RE,
  CC_CODE_ATTR_RE,
  CC_CVC_ATTR_RE,
  CC_EXP_ATTR_RE,
  CC_EXP_MONTH_ATTR_END_RE,
  CC_EXP_MONTH_ATTR_RE,
  CC_EXP_YEAR_ATTR_END_RE,
  CC_EXP_YEAR_ATTR_RE,
  CC_NAME_ATTR_RE,
  CC_NUMBER_ATTR_RE,
  CC_OUTLIER_ATTR_RE,
  CC_PREFIX_ATTR_RE,
  CONFIRM_ACTION_ATTR_END_RE,
  CONFIRM_ACTION_RE,
  CREATE_ACTION_ATTR_END_RE,
  CREATE_ACTION_RE,
  CURRENT_VALUE_RE,
  DOCUMENT_ATTR_RE,
  EMAIL_ATTR_RE,
  EMAIL_RE,
  HIDDEN_ATTR_RE,
  IDENTITY_ADDRESS_ATTR_RE,
  IDENTITY_ADDRESS_LINES_ATTR_END_RE,
  IDENTITY_CITY_ATTR_RE,
  IDENTITY_COUNTRY_ATTR_RE,
  IDENTITY_COUNTRY_CODE_ATTR_RE,
  IDENTITY_FIRSTNAME_ATTR_RE,
  IDENTITY_FULLNAME_ATTR_RE,
  IDENTITY_LASTNAME_ATTR_RE,
  IDENTITY_MIDDLENAME_ATTR_RE,
  IDENTITY_ORGANIZATION_ATTR_RE,
  IDENTITY_STATE_ATTR_RE,
  IDENTITY_TELEPHONE_ATTR_RE,
  IDENTITY_TELEPHONE_OUTLIER_ATTR_RE,
  IDENTITY_ZIPCODE_ATTR_RE,
  IFRAME_FIELD_ATTR_RE,
  LOGIN_RE,
  MULTI_STEP_RE,
  NEWSLETTER_ATTR_RE,
  NEWSLETTER_RE,
  OAUTH_ATTR_RE,
  OTP_ATTR_RE,
  OTP_OUTLIER_ATTR_RE,
  OTP_OUTLIER_RE,
  PASSWORD_OUTLIER_RE,
  PASSWORD_RE,
  PAYMENT_ATTR_RE,
  RECOVERY_RE,
  REGISTER_RE,
  REMEMBER_ACTION_RE,
  RESET_ACTION_RE,
  SEARCH_ACTION_RE,
  STEP_ACTION_RE,
  TEL_RE,
  TOS_RE,
  TROUBLE_RE,
  TWO_FA_ATTR_RE,
  TWO_FA_RE,
  USERNAME_ATTR_RE,
  USERNAME_OUTLIER_RE,
  USERNAME_RE,
} from "../dictionary/generated/dictionary";
import { EMAIL_VALUE_RE, TEL_VALUE_RE, USERNAME_VALUE_RE } from "../dictionary/source/patterns";
import { and, not, or } from "./combinators";

export type ReTest = (str: string) => boolean;
type ReSanityOptions = { maxLength?: number; minLength?: number };

/* in case we run into some very long strings,
 * we want to avoid triggering the regexp at all
 * to avoid unnecessary slowdowns */
export const reSanityCheck =
  (cb: (str: string) => boolean, options: ReSanityOptions) =>
  (str: string): boolean => {
    if (options.maxLength && str.length > options.maxLength) return false;
    if (options.minLength && str.length < options.minLength) return false;
    return cb(str);
  };

export const test = (re: RegExp, options?: ReSanityOptions) => reSanityCheck((str: string) => re.test(str), options ?? { maxLength: 5_000, minLength: 2 });

const notRe = (reg: RegExp, options?: ReSanityOptions) => (str: string) => !test(reg, options)(str);
const andRe = (reg: RegExp[], options?: ReSanityOptions) => and(...reg.map((re) => test(re, options)));
const orRe = (reg: RegExp[], options?: ReSanityOptions) => or(...reg.map((re) => test(re, options)));

export const matchLogin = test(LOGIN_RE);
export const matchRegister = test(REGISTER_RE);
export const matchUsername = test(USERNAME_RE);
export const matchUsernameAttr = orRe([USERNAME_ATTR_RE, USERNAME_RE]);
export const matchUsernameValue = test(USERNAME_VALUE_RE);
export const matchUsernameOutlier = test(USERNAME_OUTLIER_RE);
export const matchEmailAttr = orRe([EMAIL_ATTR_RE, EMAIL_RE]);
export const matchEmailValue = test(EMAIL_VALUE_RE, { maxLength: 230, minLength: 5 });
export const matchEmail = or(test(EMAIL_RE), matchEmailValue);
export const matchTel = test(TEL_RE);
export const matchTelValue = test(TEL_VALUE_RE, { maxLength: 25, minLength: 6 });
export const matchRememberMe = test(REMEMBER_ACTION_RE);
export const matchTOS = test(TOS_RE);
export const matchTrouble = test(TROUBLE_RE);
export const matchRecovery = orRe([RECOVERY_RE, TROUBLE_RE]);
export const matchMultiStep = test(MULTI_STEP_RE);
export const matchStepAction = orRe([STEP_ACTION_RE, MULTI_STEP_RE]);
export const matchCreateAction = test(CREATE_ACTION_RE);
export const matchConfirmAction = test(CONFIRM_ACTION_RE);
export const matchOAuth = test(OAUTH_ATTR_RE);
export const matchCaptcha = test(CAPTCHA_ATTR_RE);
export const matchSearchAction = test(SEARCH_ACTION_RE);
export const matchPasswordReset = and(andRe([PASSWORD_RE, RESET_ACTION_RE]), notRe(CONFIRM_ACTION_RE));
export const matchPasswordResetAttr = and(matchPasswordReset, notRe(CONFIRM_ACTION_ATTR_END_RE));
export const matchPasswordCreate = and(andRe([PASSWORD_RE, CREATE_ACTION_RE]), notRe(CONFIRM_ACTION_RE));
export const matchPasswordCreateAttr = and(matchPasswordCreate, notRe(CONFIRM_ACTION_ATTR_END_RE));
export const matchPasswordConfirm = andRe([PASSWORD_RE, CONFIRM_ACTION_RE]);
export const matchPasswordConfirmAttr = and(andRe([PASSWORD_RE, CONFIRM_ACTION_RE]), notRe(CREATE_ACTION_ATTR_END_RE));
export const matchPasswordCurrent = and(andRe([PASSWORD_RE, CURRENT_VALUE_RE]), notRe(CONFIRM_ACTION_RE));
export const matchPasswordCurrentAttr = and(matchPasswordCurrent, notRe(CONFIRM_ACTION_ATTR_END_RE));
export const matchPasswordOutlier = test(PASSWORD_OUTLIER_RE);
export const matchHidden = test(HIDDEN_ATTR_RE);
export const matchTwoFa = orRe([TWO_FA_RE, TWO_FA_ATTR_RE]);
export const matchOtpAttr = test(OTP_ATTR_RE);
export const matchOtpOutlier = test(OTP_OUTLIER_ATTR_RE);
export const matchOtpFieldOutlier = orRe([OTP_OUTLIER_ATTR_RE, USERNAME_RE]);
export const matchOtpOutlierAction = test(OTP_OUTLIER_RE);
export const matchAuthenticator = test(AUTHENTICATOR_ATTR_RE);
export const matchMFAAttr = test(TWO_FA_ATTR_RE);
export const matchMFA = orRe([TWO_FA_RE, TWO_FA_ATTR_RE, OTP_ATTR_RE]);
export const matchNewsletter = test(NEWSLETTER_RE);
export const matchNewsletterAttr = orRe([NEWSLETTER_RE, NEWSLETTER_ATTR_RE]);

/** Avoid matching [name] form attribute specifiers ie: address[0][name] */
export const matchFullName = and(test(IDENTITY_FULLNAME_ATTR_RE), (str) => !str.includes("[name]"));
export const matchFirstName = test(IDENTITY_FIRSTNAME_ATTR_RE);
export const matchMiddleName = test(IDENTITY_MIDDLENAME_ATTR_RE);
export const matchLastName = test(IDENTITY_LASTNAME_ATTR_RE);
export const matchTelephone = and(test(IDENTITY_TELEPHONE_ATTR_RE), notRe(IDENTITY_TELEPHONE_OUTLIER_ATTR_RE));
export const matchOrganization = test(IDENTITY_ORGANIZATION_ATTR_RE);
export const matchCity = test(IDENTITY_CITY_ATTR_RE);
export const matchZipCode = test(IDENTITY_ZIPCODE_ATTR_RE);
export const matchState = test(IDENTITY_STATE_ATTR_RE);
export const matchCountry = and(test(IDENTITY_COUNTRY_ATTR_RE), notRe(IDENTITY_COUNTRY_CODE_ATTR_RE));
export const matchAddress = and(test(IDENTITY_ADDRESS_ATTR_RE), notRe(IDENTITY_ADDRESS_LINES_ATTR_END_RE));

export const matchCCName = test(CC_NAME_ATTR_RE);
export const matchCCFirstName = andRe([CC_PREFIX_ATTR_RE, IDENTITY_FIRSTNAME_ATTR_RE]);
export const matchCCLastName = andRe([CC_PREFIX_ATTR_RE, IDENTITY_LASTNAME_ATTR_RE]);
export const matchCCNumber = test(CC_NUMBER_ATTR_RE);
export const matchCCSecurityCode = andRe([CC_CODE_ATTR_RE, CC_PREFIX_ATTR_RE]);
export const matchCCV = test(CC_CVC_ATTR_RE);
export const matchCCOutlier = orRe([CC_OUTLIER_ATTR_RE, DOCUMENT_ATTR_RE]);
export const matchCCExp = and(test(CC_EXP_ATTR_RE), notRe(DOCUMENT_ATTR_RE));
export const matchCCExpMonth = and(test(CC_EXP_MONTH_ATTR_RE), not(test(CC_EXP_YEAR_ATTR_END_RE)));
export const matchCCExpYear = and(test(CC_EXP_YEAR_ATTR_RE), not(test(CC_EXP_MONTH_ATTR_END_RE)));

export const matchPayment = test(PAYMENT_ATTR_RE);
export const matchIFrameField = orRe([IFRAME_FIELD_ATTR_RE, PAYMENT_ATTR_RE]);
